/**
 * Sales tax on a staff order: the storefront's rule
 * (backend/services/salesTax/flatRate.js), so a staff order and a storefront
 * order for the same shop never disagree by a cent.
 *
 *   taxed      items (add-ons are items here), the delivery fee, the
 *              priority (timed) fee
 *   not taxed  tips, gift-card purchases
 *   rounding   once per order, on the sum of the taxed lines, half-up to the
 *              cent on integer cents. Multiplying dollars by a float rate
 *              charged 1 cent less on about 1 amount in 200 at 6%
 *              ($2.75 -> $0.16 instead of $0.17).
 *
 *   discount   an online store order's discount code (order_discounts) comes
 *              off the taxed amount first, as flatRate.js does at checkout;
 *              the edit form passes it so re-taxing that order keeps it. The
 *              new-order form has no discount (0).
 */

// Same arithmetic as flatRate.js taxCents (a parity test pins it): the rate
// in millionths, so cents x rate never goes through a float.
export function taxCents(taxableCents, rate) {
  if (taxableCents <= 0) return 0;
  const millionths = Math.round(rate * 1e6);
  return Math.floor((taxableCents * millionths + 500000) / 1000000);
}

export const toCents = (dollars) => Math.round((Number(dollars) || 0) * 100);

/**
 * Tax in dollars (a whole number of cents) on a staff order.
 * @param {number} itemsSubtotal    sum of item price x quantity, dollars
 * @param {number} deliveryFee      dollars
 * @param {number} priorityTimeFee  dollars
 * @param {number|null} rate        a fraction (0.06 = 6%); null (not set) taxes nothing
 * @param {boolean} exempt          tax-exempt customer or override
 * @param {number} discount         dollars off before tax (a storefront code), default 0
 */
export function staffOrderTax({ itemsSubtotal = 0, deliveryFee = 0, priorityTimeFee = 0, rate, exempt = false, discount = 0 }) {
  if (exempt || !rate) return 0;
  return taxCents(toCents(itemsSubtotal) + toCents(deliveryFee) + toCents(priorityTimeFee) - toCents(discount), rate) / 100;
}

/**
 * The rate to tax an existing order at when it is edited: the rate stored on
 * the order (orders.sales_tax_rate, recorded when it was placed), so a later
 * change to the shop's rate never re-taxes old orders. Orders that stored no
 * rate (web orders before Phase 4, imports) fall back to the shop's current
 * rate, else 0 (not set: no tax).
 */
export function orderTaxRate(order, shopRate) {
  const stored = order?.salesTaxRate;
  if (stored !== null && stored !== undefined && stored !== '' && Number.isFinite(Number(stored))) return Number(stored);
  return shopRate ?? 0;
}

/**
 * A tax rate (a fraction) as the label staff see: the real rate to the
 * thousandth of a percent, no trailing zeros (0.0925 -> "9.25%", 0.08 -> "8%").
 * Display only; tax is computed from the rate itself.
 */
export function formatTaxRate(rate) {
  return `${+(Number(rate) * 100).toFixed(3)}%`;
}
